import {
  areMaterialCharacteristicsEqual,
  groupAdjacentTeeth,
  isToothScopedItem,
  ItemTypeEnum,
  MaterialCharacteristics,
  Product,
  ProductTypeEnum,
} from "@jasper/client-models";

// A single-tooth crown, the only kind of product that can be merged into a splinted crown
export type SplintableCrown = {
  product: Product;
  tooth: number;
  materialCharacteristics: MaterialCharacteristics;
};

// Adjacent crowns sharing the same material and shade, ordered by tooth position
export type SplintingSuggestion = {
  key: string;
  crowns: SplintableCrown[];
  materialCharacteristics: MaterialCharacteristics;
};

const toSplintableCrown = (product: Product): SplintableCrown | undefined => {
  const [item] = product.items;

  if (
    product.type !== ProductTypeEnum.crown ||
    product.teeth.length !== 1 ||
    product.items.length !== 1 ||
    item.type !== ItemTypeEnum.crown ||
    !isToothScopedItem(item) ||
    !item.materialCharacteristics
  ) {
    return undefined;
  }

  return {
    product,
    tooth: item.tooth,
    materialCharacteristics: item.materialCharacteristics,
  };
};

// Manufacturing preferences are not compared: the splinted crown keeps the ones of its first crown.
export const findSplintingSuggestions = (
  products: Product[]
): SplintingSuggestion[] => {
  const crowns = products
    .map(toSplintableCrown)
    .filter((crown): crown is SplintableCrown => !!crown);

  const crownsByCharacteristics: SplintableCrown[][] = [];
  crowns.forEach(crown => {
    const group = crownsByCharacteristics.find(([firstCrown]) =>
      areMaterialCharacteristicsEqual(
        firstCrown.materialCharacteristics,
        crown.materialCharacteristics
      )
    );

    if (group) {
      group.push(crown);
    } else {
      crownsByCharacteristics.push([crown]);
    }
  });

  return crownsByCharacteristics.flatMap(group =>
    groupAdjacentTeeth(group.map(crown => crown.tooth))
      .filter(teeth => teeth.length >= 2)
      .map(teeth => {
        const runCrowns = teeth.map(tooth =>
          group.find(crown => crown.tooth === tooth)!
        );

        return {
          key: teeth.join("-"),
          crowns: runCrowns,
          materialCharacteristics: runCrowns[0].materialCharacteristics,
        };
      })
  );
};
